import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  InstantSearch,
  Hits,
  SearchBox,
  ScrollTo,
  Pagination,
  Stats,
  Configure,
  connectStateResults,
} from 'react-instantsearch-dom';
import algoliasearch from 'algoliasearch';
import { css } from '@emotion/core';
import { useStaticQuery, graphql } from 'gatsby';
import _ from 'lodash/fp';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Location } from '@reach/router';
import qs from 'qs';
import SVG from 'react-inlinesvg';
import SearchPageHit from '../../molecules/SearchPageHit/SearchPageHit';
import {
  breakpoints,
  buttonColors,
  colors,
  fontFamilies,
  fontSizes,
  fontWeights,
  unitConverter as uc,
} from '../../../styles/base';
import algoliaConfig from '../../../data/algolia.json';
import PageWrapper from '../PageWrapper/PageWrapper';
import { Heading2 } from '../../atoms/Headings/Headings';
import Text from '../../atoms/Text/Text';
import sanityJson from '../../../../../backend/sanity.json';
import Link from '../../atoms/Link/Link';
import { getAlgoliaIndexName } from '../../../../../backend/config/intlConfig';

const algoliaClient = algoliasearch(
  algoliaConfig.ALGOLIA_APP_ID,
  algoliaConfig.ALGOLIA_SEARCH_API_KEY
);

/*
    Overrides default client's `search` method
    to avoid queries with empty values
*/
const createSearchClient = client => {
  return {
    ...client,
    search(requests) {
      if (
        requests.every(
          ({ params }) => !params.query && _.isEmpty(params.facetFilters)
        )
      ) {
        return Promise.resolve({
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            processingTimeMS: 0,
          })),
        });
      }
      return client.search(requests);
    },
  };
};

const searchClient = createSearchClient(algoliaClient);

const algoliaFormCss = css`
  .ais-SearchBox form {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${uc('50px')};

    @media (${breakpoints.tablet}) {
      display: block;
      width: 100%;
      margin-right: 0;
    }
  }
`;

const algoliaInputCss = css`
  .ais-SearchBox-input {
    display: flex;
    width: 100%;
    height: ${uc('60px')};
    padding-right: ${uc('25px')};
    color: ${colors.primary};
    font-size: ${uc('42px')};
    font-family: ${fontFamilies.proximaSoft};
    line-height: normal;
    background-color: transparent;
    border-top-color: transparent;
    border-right-color: transparent;
    border-bottom-color: ${colors.primary};
    border-left-color: transparent;
    border-radius: ${uc('0px')};
    outline: none;
    -webkit-appearance: none;

    @media (${breakpoints.tablet}) {
      display: block;
      height: ${uc('50px')};
      margin-bottom: ${uc('10px')};
      font-size: ${uc('20px')};
    }
  }
`;

const algoliaSubmitCss = css`
  .ais-SearchBox-submit {
    width: ${uc('120px')};
    height: ${uc('43.19px')};
    margin-right: ${uc('10px')};
    padding: ${uc('12px 20px')};
    overflow: hidden;
    color: ${buttonColors.accent.color};
    font-weight: ${fontWeights.semiBold};
    font-family: ${fontFamilies.proximaSoft};
    letter-spacing: ${uc('0.5px')};
    text-transform: lowercase;
    text-decoration: none;
    background: ${buttonColors.accent.background};
    border: none;
    border-color: ${buttonColors.accent.background};
    border-radius: ${uc('14px')};
    cursor: pointer;
    transition: 0.3s;

    &:active,
    &:focus,
    &:hover {
      color: ${buttonColors.accent.colorHover};
      background: ${buttonColors.accent.backgroundHover};
      border-color: ${buttonColors.accent.backgroundHover};
      outline: none;
    }

    &::after {
      content: 'search';
    }

    @media (${breakpoints.tablet}) {
      display: inline-block;
      width: 100%;
    }
  }

  .ais-SearchBox-submitIcon {
    display: none;
  }
`;

const algoliaResetCss = css`
  .ais-SearchBox-reset {
    position: absolute;
    top: ${uc('25px')};
    right: ${uc('140px')};
    width: ${uc('20px')};
    height: ${uc('20px')};
    background-color: transparent;
    border: none;
    outline: none;

    @media (${breakpoints.tablet}) {
      top: ${uc('15px')};
      right: ${uc('0px')};
      width: ${uc('20px')};
    }

    :hover {
      cursor: pointer;
    }
  }

  .ais-SearchBox-resetIcon {
    width: ${uc('15px')};
    height: ${uc('15px')};
    fill: ${colors.primary};
  }
`;

const algoliaListCss = css`
  ul.ais-Hits-list {
    width: 100%;
    list-style-type: none;

    li.ais-Hits-item {
      margin-bottom: ${uc('20px')};
    }
  }
`;

const algoliaPaginationCss = css`
  .ais-Pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${uc('20px')};
    background-color: transparent;
  }

  .ais-Pagination-list {
    list-style-type: none;
  }

  .ais-Pagination-item {
    display: inline;
    margin: ${uc('0px')};
    padding: ${uc('10px')};
    font-size: ${fontSizes.sixteen};
    background-color: ${colors.white};
    border: solid ${uc('1px')} ${colors.lightGray.one};
  }

  .ais-Pagination-item :hover {
    background-color: ${colors.lightGray.one};
  }

  .ais-Pagination-link {
    color: ${colors.darkBlue};
    font-family: ${fontFamilies.roboto};
  }

  .ais-Pagination-item--selected {
    background-color: ${colors.lightGray.two};
  }
`;

const algoliaStatsCss = css`
  .ais-Stats {
    margin-bottom: ${uc('10px')};

    .ais-Stats-text {
      color: ${colors.darkBlue};
      font-style: italic;
    }
  }
`;

const instantSearchCss = css`
  .ais-InstantSearch__root {
    width: 100%;
  }

  ${algoliaFormCss}

  ${algoliaInputCss}

  ${algoliaSubmitCss}

  ${algoliaResetCss}

  ${algoliaListCss}

  ${algoliaPaginationCss}

  ${algoliaStatsCss}
`;

const iconCss = css`
  fill: ${colors.white};

  svg {
    display: inline;
    width: ${uc('12px')};
    height: ${uc('12px')};
  }

  path {
    fill: ${colors.white};
  }
`;

const filterContainerCss = css`
  display: flex;
  flex-direction: row;
  margin-bottom: ${uc('15px')};
`;

const filterCss = css`
  display: flex;
  padding: ${uc('10px')};
  color: ${colors.white};
  background-color: ${colors.primary};
  border-radius: ${uc('15px')};

  p {
    margin-right: ${uc('10px')};
    margin-bottom: ${uc('0px')};
  }

  button {
    background-color: ${colors.transparent};
    border: none;
    outline: none;
    cursor: pointer;
  }
`;

const listCss = css`
  list-style-type: none;
`;

const Results = connectStateResults(
  ({ notFoundMessage, searchResults, suggestionLinks, suggestionMessage }) => {
    if (searchResults) {
      if (searchResults.nbHits !== 0) {
        return <Hits hitComponent={SearchPageHit} />;
      }
      if (searchResults.query) {
        return (
          <div>
            <Heading2>
              {notFoundMessage.replace(':query:', searchResults.query)}
            </Heading2>
            {suggestionLinks && (
              <div>
                <Text>{suggestionMessage}</Text>
                <ul css={listCss}>
                  {suggestionLinks.map(link => {
                    if (
                      _.getOr('', 'link.0.__typename', link) ===
                        'SanityRoute' &&
                      !_.getOr(false, 'link.0.enabled', link)
                    ) {
                      return null;
                    }
                    return (
                      <li key={link._key}>
                        <Link to={link}>{link.text}</Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      }
      return null;
    }
    return null;
  }
);

Results.propTypes = {
  searchResults: PropTypes.instanceOf(Object),
  suggestionLinks: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  notFoundMessage: PropTypes.string.isRequired,
  suggestionMessage: PropTypes.string.isRequired,
};

const SearchInput = ({ location: { search }, suggestionLinks }) => {
  const queryResult = useStaticQuery(graphql`
    {
      placeholder: sanityStaticText(name: { eq: "searchInputPlaceholder" }) {
        content
      }
      searchButton: sanityStaticText(name: { eq: "searchButton" }) {
        content
      }
      searchStats: sanityStaticText(name: { eq: "searchStats" }) {
        content
      }
      notMatchingResults: sanityStaticText(name: { eq: "notMatchingResults" }) {
        content
      }
      searchSuggestionsMessage: sanityStaticText(
        name: { eq: "searchSuggestionsMessage" }
      ) {
        content
      }
    }
  `);
  const getQueryResultString = _.getOr('', _.placeholder, queryResult);
  const searchStats = getQueryResultString('searchStats.content');
  const translations = {
    submitTitle: getQueryResultString('searchButton.content'),
    placeholder: getQueryResultString('placeholder.content'),
  };
  const notFoundMessage = getQueryResultString('notMatchingResults.content');
  const searchSuggestionsMessage = getQueryResultString(
    'searchSuggestionsMessage.content'
  );
  const searchQuery = qs.parse(search, { ignoreQueryPrefix: true });
  const [facetFilters, setFacetFilters] = useState(
    _.pipe(
      _.getOr({}, 'facetFilters'),
      _.entries,
      _.map(([key, value]) => {
        return `${key}:${value}`;
      })
    )(searchQuery)
  );

  const onClickRemoveFacetFilter = facetFilter => () => {
    setFacetFilters(_.reject(_facetFilter => _facetFilter === facetFilter));
  };

  const algoliaIndex = getAlgoliaIndexName(sanityJson.api.dataset);
  return (
    <PageWrapper>
      <div css={instantSearchCss}>
        <InstantSearch indexName={algoliaIndex} searchClient={searchClient}>
          <ScrollTo>
            <SearchBox
              searchAsYouType={false}
              translations={translations}
              defaultRefinement={searchQuery.q}
              showLoadingIndicator
            />
            {!_.isEmpty(facetFilters) && (
              <Fragment>
                <Configure facetFilters={facetFilters} />
                <div css={filterContainerCss}>
                  {_.map(facetFilter => {
                    const [, value] = facetFilter.split(':');
                    const label = `${_.startCase(value)}`;
                    return (
                      <div key={facetFilter} css={filterCss}>
                        <p>{label}</p>
                        <button
                          type="button"
                          onClick={onClickRemoveFacetFilter(facetFilter)}
                        >
                          <span css={iconCss}>
                            <SVG src="/svg/close-button.svg" />
                          </span>
                        </button>
                      </div>
                    );
                  }, facetFilters)}
                </div>
              </Fragment>
            )}
            <Stats
              translations={{
                stats(nbHits, timeSpentMS) {
                  if (nbHits > 0) {
                    return searchStats
                      .replace(':numberOfResults:', `${nbHits}`)
                      .replace(':timeSpent:', `${timeSpentMS}`);
                  }
                  return '';
                },
              }}
            />
            <Results
              notFoundMessage={notFoundMessage}
              suggestionMessage={searchSuggestionsMessage}
              suggestionLinks={suggestionLinks}
            />
            <Pagination />
          </ScrollTo>
        </InstantSearch>
      </div>
    </PageWrapper>
  );
};

SearchInput.propTypes = {
  suggestionLinks: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
  location: PropTypes.instanceOf(Object).isRequired,
};

const AlgoliaSearchInput = ({ suggestionLinks }) => {
  return (
    <Location>
      {({ location }) => (
        <SearchInput location={location} suggestionLinks={suggestionLinks} />
      )}
    </Location>
  );
};

AlgoliaSearchInput.propTypes = {
  suggestionLinks: PropTypes.arrayOf(PropTypes.instanceOf(Object)).isRequired,
};

export default AlgoliaSearchInput;
